import {
    PermissionCheckerService,
    FeatureCheckerService,
    LocalizationService,
    MessageService,
    AbpMultiTenancyService,
    NotifyService,
    SettingService,
} from 'abp-ng2-module';
import { Component, Injector, OnDestroy } from '@angular/core';
import { AppConsts } from '@shared/AppConsts';
import { AppUrlService } from '@shared/common/nav/app-url.service';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { AppUiCustomizationService } from '@shared/common/ui/app-ui-customization.service';
import { PrimengTableHelper } from 'shared/helpers/PrimengTableHelper';
import { UiCustomizationSettingsDto } from '@shared/service-proxies/service-proxies';
import '@shared/service-proxies/tenant-login-info-dto-extensions';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgxSpinnerTextService } from '@app/shared/ngx-spinner-text.service';
import { AttachmentInput } from '@shared/service-proxies/service-proxies';
import { FileDownloadService } from '@shared/utils/file-download.service';
import { FileUploadService } from '@shared/utils/file-upload.service';
import { DateTime } from "luxon";
import { NgModel } from "@angular/forms";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ReportDownloadService } from '@shared/utils/report-download.service';

interface AbpEventSubscription {
    eventName: string;
    callback: (...args: any[]) => void;
}

@Component({
    template: '',
})
export abstract class AppComponentBase implements OnDestroy {
    localizationSourceName = AppConsts.localization.defaultLocalizationSourceName;
    injector: Injector
    defaultDebounceTime = AppConsts.debounceTime.default;
    maxExcelUploadSize = AppConsts.maximumExcelUploadSize;

    appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: AppConsts.appInsightsInstrumentationKey,
            // Enable for SPA or else it will all look like a single request.
            enableAutoRouteTracking: true
        }
    });

    localization: LocalizationService;
    permission: PermissionCheckerService;
    feature: FeatureCheckerService;
    notify: NotifyService;
    setting: SettingService;
    message: MessageService;
    multiTenancy: AbpMultiTenancyService;
    appSession: AppSessionService;
    primengTableHelper: PrimengTableHelper;
    ui: AppUiCustomizationService;
    appUrlService: AppUrlService;
    spinnerService: NgxSpinnerService;
    exclusionFieldList: string[] = [];
    allowedIdFieldList: string[] = [];
    fileDownloadService: FileDownloadService;
    fileUploadService: FileUploadService;
    sortedInclusionFieldList: string[] = [];
    reportDownloadService: ReportDownloadService;    
    eventSubscriptions: AbpEventSubscription[] = [];

    private ngxSpinnerTextService: NgxSpinnerTextService;

    constructor(injector: Injector) {
        this.injector = injector;
        this.localization = injector.get(LocalizationService);
        this.permission = injector.get(PermissionCheckerService);
        this.feature = injector.get(FeatureCheckerService);
        this.notify = injector.get(NotifyService);
        this.setting = injector.get(SettingService);
        this.message = injector.get(MessageService);
        this.multiTenancy = injector.get(AbpMultiTenancyService);
        this.appSession = injector.get(AppSessionService);
        this.ui = injector.get(AppUiCustomizationService);
        this.appUrlService = injector.get(AppUrlService);
        this.primengTableHelper = new PrimengTableHelper();
        this.spinnerService = injector.get(NgxSpinnerService);
        this.ngxSpinnerTextService = injector.get(NgxSpinnerTextService);

        this.exclusionFieldList = this.l('ClaimsExclusionFieldList').split('|');
        this.allowedIdFieldList = this.l('ClaimsAllowedIdFieldList').split('|');
        this.fileDownloadService = injector.get(FileDownloadService);
        this.fileUploadService = injector.get(FileUploadService);
        this.reportDownloadService = injector.get(ReportDownloadService);
        this.sortedInclusionFieldList = this.l('RemittanceFieldListSortedInclusion').split('|');
    }

    get currentTheme(): UiCustomizationSettingsDto {
        return this.appSession.theme;
    }

    get appLogoSkin(): string{
        if (this.currentTheme.isTopMenuUsed || this.currentTheme.isTabMenuUsed)
        {
            return this.currentTheme.baseSettings.layout.darkMode ? "light" : "dark";
        }

        return this.currentTheme.baseSettings.menu.asideSkin;
    }

    get containerClass(): string {
        if (this.layoutType === 'fluid') {
            return 'app-container container-fluid';
        } else if (this.layoutType === 'fixed' || this.layoutType === 'fluid-xxl') {
            return 'app-container container-xxl';
        }

        return 'app-container container';
    }
    
    get footerContainerClass(): string {
        if (this.appSession.theme.baseSettings.footer.footerWidthType === 'fluid') {
            return 'app-footer-container container-fluid';
        } else if (this.appSession.theme.baseSettings.footer.footerWidthType === 'fixed' || this.appSession.theme.baseSettings.layout.layoutType === 'fluid-xxl') {
            return 'app-footer-container container-xxl';
        }  
        return 'app-container container'
    }

    get layoutType(): string {
        return this.appSession.theme.baseSettings.layout.layoutType;
    }

    ngOnDestroy(): void {
        this.unSubscribeAllEvents();
    }

    flattenDeep(array) {
        return array.reduce(
            (acc, val) => (Array.isArray(val) ? acc.concat(this.flattenDeep(val)) : acc.concat(val)),
            []
        );
    }

    l(key: string, ...args: any[]): string {
        args.unshift(key);
        args.unshift(this.localizationSourceName);
        return this.ls.apply(this, args);
    }

    ls(sourcename: string, key: string, ...args: any[]): string {
        let localizedText = this.localization.localize(key, sourcename);

        if (!localizedText) {
            localizedText = key;
        }

        if (!args || !args.length) {
            return localizedText;
        }

        args.unshift(localizedText);
        return abp.utils.formatString.apply(this, this.flattenDeep(args));
    }

    isGranted(permissionName: string): boolean {
        return this.permission.isGranted(permissionName);
    }

    isGrantedAny(...permissions: string[]): boolean {
        if (!permissions) {
            return false;
        }

        for (const permission of permissions) {
            if (this.isGranted(permission)) {
                return true;
            }
        }

        return false;
    }

    isNullOr(obj: any, param: any): boolean {
        return obj == param || obj == null;
    }


    s(key: string): string {
        return abp.setting.get(key);
    }

    appRootUrl(): string {
        return this.appUrlService.appRootUrl;
    }

    showMainSpinner(text?: string): void {
        this.ngxSpinnerTextService.setText(text);
        this.spinnerService.show();
    }

    isInExclusionFieldList(field: string): boolean {
        return this.exclusionFieldList.indexOf(field) > -1;
    }
    isPartialInExclusionFieldList(field: string): boolean {
        let match: boolean = false;
        this.exclusionFieldList.map(x => {
            if (field.indexOf(x) > -1)
                match = true;
        })
        return match;
    }

    getSortedIncludedColumn(items): any[] {
        const columns = [];
        for (const prop in items) {
            if (!this.isInInclusionFieldList(prop)) {
                columns.push(this.mapPropToColumn(prop));
            }
        }
        return this.orderColumnHeaders(columns);;
    }

    mapPropToColumn(prop): any {
        return { field: prop, header: this.l(prop) };
    }

    isInInclusionFieldList(field: string): boolean {
        return !(this.sortedInclusionFieldList.indexOf(field) > -1);
    }

    orderColumnHeaders(headers): any[] {
        return headers.sort((a, b) => {
            if (this.sortedInclusionFieldList.indexOf(a.field) > this.sortedInclusionFieldList.indexOf(b.field)) {
                return 1;
            }
            return -1;
        });
    }

    calculateColumnWidth(columnCount: number): string {
        return (100.0 / (columnCount + 1)).toString() + '%';
    }

    /**
     * Returns the keys from a given object
     * @param items
     */
    getColumns(items): any[] {
        const columns = [];
        for (const prop in items) {
            if (!this.isInExclusionFieldList(prop))
                columns.push({ field: prop, header: this.l(prop) });
        }
        return columns;
    }

    isInAllowedIdFieldList(field: string): boolean {
        return this.allowedIdFieldList.indexOf(field) > -1;
    }

    isDateField(field: string): boolean {
        return field.toLowerCase().indexOf('date') > -1 || field.toLowerCase().indexOf('creationtime') > -1;
    }

    cloneObject(obj: any): any {
        return JSON.parse(JSON.stringify(obj));
    }

    cloneObjectWithUndefinedAsNull(obj: any): any {
        return JSON.parse(JSON.stringify(obj, function (k, v) { if (v === undefined) { return null; } return v; }));
    }

    // downloadFile(file: AttachmentInput): void {
    //     this.fileDownloadService.downloadFile(this.getFileUrl(file), file.fileName).subscribe(result => {
    //         if (result != 'Success') {
    //             this.message.error(this.l('DownloadAttachmentError') + ' ' + file.fileName, this.l('DownloadAttachment'));
    //         }
    //     })
    // }

    getFileUrl(file: AttachmentInput, isDelete?: boolean): string {
        let url: string = "?";
        let downloadAttachmentUrl = AppConsts.remoteServiceBaseUrl + (!isDelete ? '/Claims/DownloadAttachment' : '/Claims/DeleteAttachment');

        Object.keys(file).forEach(key => {
            url += key + '=' + file[key] + '&';
        });

        return encodeURI(downloadAttachmentUrl + url);
    }

    isValidDate(date: any): boolean {
        return (new Date(date)).toString() != 'Invalid Date' && date != null;
    }

    allowedClaimFields(column: string): boolean {
        if (this.isInAllowedIdFieldList(column)) {
            return true;
        } else if (column.toLowerCase().endsWith('id') || this.isInExclusionFieldList(column)) {
            return false;
        } else {
            return true;
        }
    }

    isNullOrUndefined(val: any): boolean {
        return val == null;
    }

    /**
     * blocks a user input component
     * @param element
     */
    blockUi(element: any): void {
        abp.ui.setBusy(element);
    }

    /**
     * unblocks user input component
     * @param element
     */
    unblockUi(element: any): void {
        abp.ui.clearBusy(element);
    }

    /**
     * Destroys each array passed in a list of arrays.
     * @param arrs
     */
    destroyArr(arrs: any[]): void {
        arrs.map((x) => {
            x.splice(0, x.length);
        })
    }

    /**
     * Adds up a single property throughout an array
     * @param arr       -- the array
     * @param prop      -- the property to total
     */
    reduceArrayAddition(arr: any[], prop: string): number {
        return (arr.map(x => x[prop]).reduce((acc, current) => acc + current))
    }

    /**
     * Used to get either current maxResult and skipCount or defaults
     * @param table
     * @param results
     */
    getMaxResultSkipCount(table: any, paginator: any): any {
        const mrSc: any = {
            maxResult: 0,
            skipCount: 0
        };

        mrSc.maxResult = table.getMaxResultCount(paginator, null) || table.defaultRecordsCountPerPage;
        mrSc.skipCount = table.getSkipCount(paginator, null) || 0;

        return mrSc;
    }

    /**
     * Used to return a friendly readable date from a date or moment object.
     * @param date
     */
    getFriendlyDateString(date: any): string {
        return new Date(date).toLocaleDateString();
    }

    /**
     * Will sort and return an array of objects in ascending order based on a property of that object.
     * @param arr
     * @param attr
     */
    sortArrayByPropAscending(arr: Array<any>, prop: string): Array<any> {
        return arr.sort((a, b) => a[prop] - b[prop]);
    }

    /**
     * Will sort and return an array of objects in descending order based on a property of that object.
     * @param arr
     * @param attr
     */
    sortArrayByPropDescending(arr: Array<any>, prop: string): Array<any> {
        return arr.sort((a, b) => b[prop] - a[prop]);
    }

    animateContainerSwap(fadingElement: JQuery, displayElement: JQuery) {
        fadingElement.animate({ opacity: 0 }, () => {
            displayElement.css({ 'display': 'block' });
            fadingElement.css({ 'display': 'none' });
            displayElement.animate({ opacity: 1 });
        })

    }

    numericKeypressOnly(event: KeyboardEvent): boolean {
        return event.charCode >= 48 && event.charCode <= 57;
    }

    /**
    *  Scroll to invoice entry
    */
    scrollToElementId(element: string): void {
        setTimeout(() => {
            let scrollElement: Element = document.getElementById(element);
            scrollElement.scrollIntoView({ behavior: "smooth" });
        }, 0);
    }

    ///**
    //* Standardizes the date input.
    //* @param date
    //*/
    //momentrize(date: DateTime): DateTime {
    //    if (!date) return null;
    //    return DateTime.fromISO(date, 'MM-DD-YYYY').startOf('day');
    //}

    registerAutoSearch(model: NgModel, callback: Function): void {
        model.valueChanges.pipe(distinctUntilChanged(), debounceTime(AppConsts.debounceTime.default)).subscribe(() => callback())
    }

    firstCharacterToUpper(val: string): string {
        return val.charAt(0).toLocaleUpperCase() + val.slice(1);
    }

    firstCharacterToLower(val: string): string {
        return val.charAt(0).toLocaleLowerCase() + val.slice(1);
    }

    hideMainSpinner(text?: string): void {
        this.spinnerService.hide();
    }

    protected subscribeToEvent(eventName: string, callback: (...args: any[]) => void): void {
        abp.event.on(eventName, callback);
        this.eventSubscriptions.push({
            eventName,
            callback,
        });
    }

    private unSubscribeAllEvents() {
        this.eventSubscriptions.forEach((s) => abp.event.off(s.eventName, s.callback));
        this.eventSubscriptions = [];
    }
}
